<ng-container *transloco="let t">
  <td iTableTd class="w-[30%] max-w-0 truncate">
    <div class="flex items-center gap-2">
      @if (!isReadOnly()) {
        <ICheckbox [(value)]="isRowSelected" />
      }
      <a
        iButton
        variant="ghost"
        size="sm"
        class="group/btn justify-start text-sm text-gray-700"
        routerLink="/dashboard/advertising"
        [queryParams]="{
          accountId: accountMarketplace().accountId,
          marketplace: accountMarketplace().marketplace,
          orgId: accountMarketplace().resourceOrganizationId,
        }"
        routerLinkActive="active"
        [iTooltip]="t('account-setting.navigate_to_advertising')"
        iTooltipPlacement="right-center"
      >
        <div class="truncate">{{ accountMarketplace().accountName }}</div>

        <IIcon name="arrowUpRight" class="invisible shrink-0 group-hover/btn:visible" />
      </a>
      @if (accountMarketplace().dspAdvertiserId) {
        <IBadge
          size="xs"
          variant="soft"
          [iTooltip]="
            t('account-name-link.dsp_advertiser_has_been_linked_to_this_account', [
              [accountMarketplace().dspAdvertiser?.name],
            ])
          "
          iTooltipPlacement="top-center"
        >
          <ng-template #labelSlot>
            <span class="block max-w-[8rem] truncate">{{ dspBadgeLabel() }}</span>
          </ng-template>
        </IBadge>
      }
    </div>
  </td>
  <td iTableTd>
    <div class="flex items-center gap-2">
      <IMarketplaceFlag [marketplace]="accountMarketplace().marketplace" />
      <span class="text-sm whitespace-nowrap text-gray-500">{{ marketplaceLabel() }}</span>
    </div>
  </td>
  <td iTableTd>
    <button
      iButton
      variant="ghost"
      size="xs"
      class="text-gray-600"
      [iTooltip]="t('account-setting.edit_account_group_name')"
      iTooltipPlacement="top-center"
      [disabled]="isReadOnly()"
      (click)="editAccountGroupName()"
    >
      {{ accountMarketplace().accountGroupName }}
    </button>
  </td>

  <td iTableTd>
    <app-account-status-badge [accountBidderStatus]="accountBidderStatus()" />
  </td>
  <td iTableTd>
    <span class="text-xs text-gray-500">{{ accountMarketplace().accountType }}</span>
  </td>
  <td iTableTd>
    @if ((overdueAmazonInvoices()?.length ?? 0) > 0) {
      <a
        iButton
        variant="ghost"
        size="xs"
        class="bg-red-50 text-red-500 hover:bg-red-100"
        routerLink="/amazon-invoices"
        [queryParams]="{
          accountId: accountMarketplace().accountId,
          marketplace: accountMarketplace().marketplace,
          orgId: accountMarketplace().resourceOrganizationId,
        }"
        routerLinkActive="active"
        [iTooltip]="
          t('account-setting.overdue_invoices_tooltip', { count: overdueAmazonInvoices()!.length }) +
          ' ' +
          t('account-setting.click_to_view_your_amazon_invoice_details')
        "
        iTooltipPlacement="bottom-center"
      >
        <IIcon name="alertTriangle" />
        <span>{{ t("account-setting.overdue_invoices") }}</span>
      </a>
    } @else if ((pendingAmazonInvoices()?.length ?? 0) > 0) {
      <a
        iButton
        variant="ghost"
        size="xs"
        class="bg-orange-50 text-orange-500 hover:bg-orange-100"
        routerLink="/amazon-invoices"
        [queryParams]="{
          accountId: accountMarketplace().accountId,
          marketplace: accountMarketplace().marketplace,
          orgId: accountMarketplace().resourceOrganizationId,
        }"
        routerLinkActive="active"
        [iTooltip]="
          t('account-setting.pending_invoices_tooltip', { count: pendingAmazonInvoices()!.length }) +
          ' ' +
          t('account-setting.click_to_view_your_amazon_invoice_details')
        "
        iTooltipPlacement="bottom-center"
      >
        <span>{{ t("account-setting.pending_invoices", { count: pendingAmazonInvoices()!.length }) }}</span>
      </a>
    }
  </td>
  <td iTableTd class="flex items-center gap-2">
    <app-activate-ads-automation-cta [accountMarketplace]="accountMarketplace()" />
    <app-activate-dsp-automation-cta
      [accountMarketplace]="accountMarketplace()"
      [organization]="organization()"
      [isAdminOrOwner]="isAdminOrOwner()"
      [label]="t('account-setting.activate_full_automation_dsp')"
    />
  </td>

  <td iTableTd>
    <button
      iButton
      [iDropdownMenuTrigger]="rowMenu"
      color="gray"
      variant="ghost"
      size="sm"
      class="opacity-0 group-hover:opacity-100"
      placement="bottom-end"
      (click)="$event.preventDefault(); $event.stopPropagation()"
    >
      <IIcon name="ellipsis" />
    </button>
    <ng-template #rowMenu>
      <IDropdownMenu>
        @if (isAdminOrOwner() && hasActiveSubscription() && !isReadOnly()) {
          <button iDropdownMenuItem (click)="manageAuthorizedUsers()">
            <IIcon name="lock" />
            {{ t("account-setting.manage_access") }}
          </button>
        }
        @if (canUpdateMaxBid()) {
          <button iDropdownMenuItem (click)="updateMaxBid()">
            <IIcon name="dollarSign" />
            {{ t("account-setting.update_max_bid") }}
          </button>
        }
        @if (
          canLoginWithAmazon() && !accountMarketplace().hasAccessToAdvertising && isAdminOrOwner() && !isReadOnly()
        ) {
          <button iDropdownMenuItem (click)="displayAmazonUserAccess()">
            <IIcon name="lock" />
            {{ t("account-setting.renew_access_by_login_with_amazon") }}
          </button>
        }
        <button iDropdownMenuItem (click)="displayAmazonUserAccess()">
          <IIcon name="lock" />
          {{ t("account-setting.display_amazon_user_access_emails") }}
        </button>
        @if (customCampaignNameAllowed() && isBidderOn() && !isReadOnly()) {
          <button iDropdownMenuItem (click)="updateCustomCampaignName()">
            <IIcon name="aLargeSmall" />
            {{ t("account-setting.update_custom_campaign_name") }}
          </button>
        }
        @if (isAdmin() && isBidderOn()) {
          <button iDropdownMenuItem (click)="updateCampaignLabel()">
            <IIcon name="tag" />
            {{ t("account-setting.edit_campaign_label") }}
          </button>
        }
        @if (isVendor() && (isAdmin() || isWrite())) {
          <button iDropdownMenuItem (click)="updateVendorSourcingMetric()">
            {{ t("account-setting.select_vendor_metrics_sourcing_manufacturing") }}
          </button>
        }
        @if (isAdmin()) {
          @if (!accountMarketplace().dspAdvertiserId) {
            <button iDropdownMenuItem (click)="linkDspAdvertisingAccount()">
              <IIcon name="link" />
              {{ t("account-setting.link_your_dsp_advertising_account") }}
            </button>
          } @else {
            <button iDropdownMenuItem (click)="displayDspAdvertisingAccountInformation()">
              <IIcon name="infoCircle" />
              {{ t("account-setting.display_dsp_advertising_account_information") }}
            </button>
            @if (hasSeatOnM19()) {
              <button
                iDropdownMenuItem
                routerLink="/amazon-invoices"
                [queryParams]="{
                  accountId: accountMarketplace().accountId,
                  marketplace: accountMarketplace().marketplace,
                  orgId: accountMarketplace().resourceOrganizationId,
                }"
              >
                <IIcon name="fileSpreadsheet" />
                {{ t("account-setting.see_amazon_invoices") }}
              </button>
            }
          }
        }
        @if (isAdminOrOwner() && !isReadOnly()) {
          @if (isDspBidderOn()) {
            <button iDropdownMenuItem (click)="stopDspBidder()">
              <IIcon name="close" />
              {{ t("account-setting.deactivate_full_automation_dsp") }}
            </button>
          } @else if (isBidderOn()) {
            <button iDropdownMenuItem (click)="stopBidder()">
              <IIcon name="close" />
              {{ t("account-setting.deactivate_full_automation") }}
            </button>
          }
        }
        @if (!accountMarketplace().isValidToken) {
          @if (!isReadOnly()) {
            <button
              iDropdownMenuItem
              (click)="grantSellingPartnerAccess()"
              [iTooltip]="t('default-layout.login_tooltip', [isVendor() ? 'Vendor Central' : 'Seller Central'])"
              iTooltipPlacement="left-center"
            >
              <IIcon name="amazon" />
              {{ t("account-setting.grant_access") }}
            </button>
          }
        } @else if (isAdmin()) {
          <button
            iDropdownMenuItem
            (click)="requestGrant()"
            [iTooltip]="t('account-setting.renew_access_on_mktpl', [isVendor() ? 'Vendor Central' : 'Seller Central'])"
            iTooltipPlacement="left-center"
          >
            <IIcon name="amazon" />
            {{ t("account-setting.renew") }}
          </button>
        }
        <IDropdownMenuSeparator />

        <button iDropdownMenuItem>
          <IToggle
            size="sm"
            (checkedChange)="toggleBrandTraffic()"
            [disabled]="!isAdminOrOwner()"
            [checked]="accountMarketplace().brandedTrafficActivated"
            [loading]="brandTrafficLoading()"
          />
          {{ t("brand-traffic.brand_traffic") }}
        </button>
        <IDropdownMenuSeparator />
        <button iDropdownMenuItem (click)="copyAccountId()">
          <IIcon name="copyO" />
          {{ t("account-setting.copy_account_id") }}
        </button>
        <IDropdownMenuSeparator />
        @if (isAdmin()) {
          <button
            iDropdownMenuItem
            variant="danger"
            [disabled]="isBidderOn()"
            (click)="deleteAccount()"
            [iTooltip]="isBidderOn() ? t('account-setting.fully_automated_accounts_cant_be_deleted') : undefined"
            iTooltipPlacement="left-center"
          >
            <IIcon name="trash" />
            {{ t("common.delete") }}
          </button>
        }
      </IDropdownMenu>
    </ng-template>
  </td>
</ng-container>
